import { useEffect, useState } from "react";
import {
  calcularPrecoComDesconto,
  carregarPromocoes,
} from "../services/promocoes";
import "../styles/promocoes.css";

const formatarMoeda = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const formasPagamento = [
  { id: "dinheiro", nome: "Dinheiro" },
  { id: "pix", nome: "PIX" },
  { id: "cartao", nome: "Cartão" },
];

function EstadoPagina({ tipo, tentarNovamente }) {
  const erro = tipo === "erro";

  return (
    <main className="promocoes-page">
      <section className="promocoes-status" aria-live="polite">
        <span className="promocoes-status-mark" aria-hidden="true">
          {erro ? "!" : "•"}
        </span>
        <h1>{erro ? "Não foi possível carregar a oferta" : "Novidades em breve"}</h1>
        <p>
          {erro
            ? "Tivemos um problema ao consultar as condições desta promoção."
            : "No momento não há um encarte promocional disponível. Volte em breve para conferir as próximas ofertas."}
        </p>
        {erro && (
          <button type="button" onClick={tentarNovamente}>
            Tentar novamente
          </button>
        )}
      </section>
    </main>
  );
}

function CardPromocao({ promocao }) {
  const [formaSelecionada, setFormaSelecionada] = useState("pix");
  const [imagemComErro, setImagemComErro] = useState(false);
  const desconto = promocao.descontos[formaSelecionada];
  const precoFinal = calcularPrecoComDesconto(promocao.preco, desconto);

  return (
    <article className="promocao-card">
      <div className="promocao-encarte">
        {promocao.imagem && !imagemComErro ? (
          <a
            href={promocao.imagem}
            target="_blank"
            rel="noreferrer"
            aria-label="Abrir encarte promocional em tamanho completo"
          >
            <img
              src={promocao.imagem}
              alt="Encarte promocional Accium"
              onError={() => setImagemComErro(true)}
            />
          </a>
        ) : (
          <div className="promocao-sem-imagem">
            <span aria-hidden="true">ACCIUM</span>
            <strong>Encarte em atualização</strong>
            <p>A imagem desta oferta estará disponível em breve.</p>
          </div>
        )}
      </div>

      <div className="promocao-detalhes">
        <span className="promocao-kicker">Condição especial</span>
        <h2>Escolha como deseja pagar</h2>
        <p className="promocao-intro">
          O valor da oferta muda conforme a forma de pagamento.
        </p>

        <label className="promocao-select-label" htmlFor={`${promocao.id}-pagamento`}>
          Forma de pagamento
        </label>
        <div className="promocao-select-wrap">
          <select
            id={`${promocao.id}-pagamento`}
            value={formaSelecionada}
            onChange={(event) => setFormaSelecionada(event.target.value)}
          >
            {formasPagamento.map((forma) => {
              const percentual = promocao.descontos[forma.id] * 100;

              return (
                <option key={forma.id} value={forma.id}>
                  {forma.nome}
                  {percentual > 0
                    ? ` — ${percentual.toLocaleString("pt-BR", {
                        maximumFractionDigits: 2,
                      })}% de desconto`
                    : " — sem desconto"}
                </option>
              );
            })}
          </select>
          <span aria-hidden="true">⌄</span>
        </div>

        <div className="promocao-preco" aria-live="polite">
          <span>Valor nesta condição</span>
          <strong>{formatarMoeda.format(precoFinal)}</strong>
          {desconto > 0 && (
            <small>
              De {formatarMoeda.format(promocao.preco)} por{" "}
              {formasPagamento.find((forma) => forma.id === formaSelecionada)?.nome}
            </small>
          )}
        </div>

        <p className="promocao-aviso">
          Consulte disponibilidade, condições e prazo da oferta diretamente com
          a equipe Accium.
        </p>
      </div>
    </article>
  );
}

function Promocoes() {
  const [estado, setEstado] = useState("carregando");
  const [promocoes, setPromocoes] = useState([]);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    document.title = "Promoções | Accium";
    setEstado("carregando");

    carregarPromocoes({ signal: controller.signal })
      .then((dados) => {
        setPromocoes(dados);
        setEstado(dados.length ? "pronto" : "vazio");
      })
      .catch((erro) => {
        if (erro.name !== "AbortError") {
          console.error("Erro ao carregar promoções:", erro);
          setEstado("erro");
        }
      });

    return () => controller.abort();
  }, [tentativa]);

  if (estado === "carregando") {
    return (
      <main className="promocoes-page">
        <section className="promocoes-loading" aria-busy="true" aria-label="Carregando promoção">
          <div className="promocoes-loading-image" />
          <div className="promocoes-loading-content">
            <span />
            <span />
            <span />
          </div>
        </section>
      </main>
    );
  }

  if (estado === "vazio" || estado === "erro") {
    return (
      <EstadoPagina
        tipo={estado}
        tentarNovamente={() => setTentativa((valor) => valor + 1)}
      />
    );
  }

  return (
    <main className="promocoes-page">
      <header className="promocoes-header">
        <span>Ofertas Accium</span>
        <h1>Promoções em destaque</h1>
        <p>Confira o encarte e simule o valor para cada forma de pagamento.</p>
      </header>

      <section className="promocoes-list" aria-label="Promoções disponíveis">
        {promocoes.map((promocao) => (
          <CardPromocao key={promocao.id} promocao={promocao} />
        ))}
      </section>
    </main>
  );
}

export default Promocoes;
